import { useEffect } from 'react';
import { useSettings } from '@/context/SettingsContext';

/**
 * ThemeApplier reads settings.appearance and sets CSS variables + document attributes
 * on the <html> root so the entire app can style off them without touching Tailwind.
 * It also lazy-loads any custom Google Font requested by the admin.
 */
const SHADOW_MAP = {
  none: 'none',
  soft: '0 1px 2px rgba(16,24,40,0.04), 0 4px 12px -6px rgba(16,24,40,0.06)',
  medium: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px -10px rgba(16,24,40,0.08)',
  strong: '0 4px 6px rgba(16,24,40,0.05), 0 24px 48px -12px rgba(15,138,67,0.20)',
};

const ANIM_SPEED_MAP = { slow: '450ms', normal: '220ms', fast: '120ms' };

const SYSTEM_FONTS = new Set(['system', 'system-ui', 'Figtree', 'Inter', 'Roboto', 'sans-serif']);

function ensureGoogleFont(family) {
  if (!family || SYSTEM_FONTS.has(family)) return;
  const id = `gfont-${family.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

export function applyAppearance(a) {
  if (!a || typeof a !== 'object') return;
  const root = document.documentElement;
  const set = (k, v) => { if (v !== undefined && v !== null && v !== '') root.style.setProperty(k, String(v)); };
  const px = (v) => (typeof v === 'number' ? `${v}px` : v);

  set('--app-bg', a.background_color);
  set('--app-surface', a.surface_color);
  set('--app-text', a.text_color);
  set('--app-muted', a.muted_text_color);
  set('--app-primary', a.primary_color);
  set('--app-primary-hover', a.primary_hover_color || a.button_hover_color);
  set('--app-accent', a.accent_color);
  set('--app-button-text', a.button_text_color);
  set('--app-button-hover', a.button_hover_color || a.primary_hover_color);
  set('--app-badge', a.badge_color);
  set('--app-border', a.border_color);

  set('--app-button-radius', px(a.button_radius));
  set('--app-card-radius', px(a.card_radius));
  set('--app-product-radius', px(a.product_image_radius));
  set('--app-grid-gap', px(a.grid_gap));
  set('--app-section-pad', px(a.section_padding));

  set('--app-banner-h', px(a.banner_height));
  set('--app-header-h', px(a.header_height));

  set('--app-search-bg', a.search_bg);
  set('--app-search-border', a.search_border);
  set('--app-search-radius', px(a.search_radius));
  set('--app-search-placeholder', a.search_placeholder_color);
  set('--app-navbar-bg', a.navbar_bg);
  set('--app-navbar-icon', a.navbar_icon_color);
  set('--app-bottom-nav-bg', a.bottom_nav_bg);
  set('--app-bottom-nav-icon', a.bottom_nav_icon_color);
  set('--app-footer-bg', a.footer_bg);
  set('--app-footer-text', a.footer_text_color);
  set('--app-whatsapp-bg', a.whatsapp_button_color);

  set('--app-card-shadow', SHADOW_MAP[a.card_shadow] || SHADOW_MAP.medium);
  set('--app-anim-speed', ANIM_SPEED_MAP[a.animation_speed] || ANIM_SPEED_MAP.normal);
  set('--app-font-size-base', px(a.font_size_base));

  const family = a.font_family;
  if (family) {
    ensureGoogleFont(family);
    root.style.setProperty('--app-font-family', SYSTEM_FONTS.has(family)
      ? `'${family}', system-ui, sans-serif`
      : `'${family}', 'Figtree', system-ui, sans-serif`);
  }

  root.dataset.appTheme = a.theme_mode || 'light';
  root.dataset.appAnimations = a.animations_enabled === false ? 'off' : 'on';
  root.dataset.appStickyHeader = a.sticky_header === false ? 'off' : 'on';
  root.dataset.appWhatsappPos = a.whatsapp_position || 'bottom-right';
  root.dataset.appProductSize = a.product_image_size || 'medium';
}

export default function ThemeApplier() {
  const { settings } = useSettings();
  useEffect(() => { applyAppearance(settings?.appearance); }, [settings?.appearance]);
  return null;
}
